import React from "react";
import { Instagram } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";
import { HelpTooltip } from "@/components/ui/help-tooltip";
import { formatInteger } from "@/utils/numberFormat";
import type { InstagramPostDetail as PostDetailType } from "./instagram.types";

interface InstagramPostDetailProps {
  data: PostDetailType;
  loading?: boolean;
}

/** Skeleton for the post detail card */
function PostDetailSkeleton(): React.ReactElement {
  return (
    <Card>
      <CardContent className="pt-5 space-y-3">
        <div className="flex gap-4">
          <Skeleton className="size-12 rounded-lg" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-3 w-24" />
          </div>
        </div>
        <Skeleton className="h-8 w-full" />
      </CardContent>
    </Card>
  );
}

export default function InstagramPostDetailCard({
  data,
  loading = false,
}: InstagramPostDetailProps): React.ReactElement {
  if (loading) return <PostDetailSkeleton />;

  return (
    <Card>
      <CardContent className="pt-5">
        <div className="overflow-x-auto">
          <table className="w-full text-xs mb-0">
            <thead>
              <tr className="border-b border-border text-muted-foreground">
                <th className="py-2 pr-3 text-left font-medium w-[60px]">
                  Postagem
                </th>
                <th className="py-2 pr-3 text-left font-medium">Condições</th>
                <th className="py-2 pr-3 text-left font-medium">Tipo</th>
                <th className="py-2 pr-3 text-right font-medium">
                  Visualizações
                </th>
                <th className="py-2 pr-3 text-right font-medium">
                  <span className="inline-flex items-center gap-1">
                    Alcance
                    <HelpTooltip
                      content="Alcance total do post"
                      triggerClassName="h-3 w-3 text-[8px]"
                    />
                  </span>
                </th>
                <th className="py-2 pr-3 text-right font-medium">TX.</th>
                <th className="py-2 pr-3 text-right font-medium">Curtidas</th>
                <th className="py-2 pr-3 text-right font-medium">
                  Comentários
                </th>
                <th className="py-2 pr-3 text-right font-medium">Salvos</th>
                <th className="py-2 pr-3 text-right font-medium">Compart.</th>
                <th className="py-2 text-right font-medium">
                  Pontuação (eficácia)
                </th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-border/50">
                <td className="py-3 pr-3">
                  {data.thumbnail ? (
                    <img
                      src={data.thumbnail}
                      alt={data.caption || "Post do Instagram"}
                      className="size-12 rounded-lg object-cover ring-1 ring-border"
                      loading="lazy"
                      referrerPolicy="no-referrer"
                      onError={(event) => {
                        event.currentTarget.style.display = "none";
                      }}
                    />
                  ) : (
                    <div className="size-12 rounded-lg bg-muted ring-1 ring-border flex items-center justify-center">
                      <Instagram className="h-5 w-5 text-muted-foreground" />
                    </div>
                  )}
                </td>
                <td className="py-3 pr-3">
                  <div className="max-w-[180px]">
                    <p
                      className="text-xs font-medium text-foreground truncate"
                      title={data.caption}
                    >
                      {data.caption}
                    </p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      {data.date}
                    </p>
                  </div>
                </td>
                <td className="py-3 pr-3">
                  <span
                    className="inline-block max-w-[90px] truncate"
                    title={data.type}
                  >
                    <Badge variant="default" className="text-[10px]">
                      {data.type}
                    </Badge>
                  </span>
                </td>
                <td className="py-3 pr-3 text-right font-medium">
                  <span title={formatInteger(data.views)}>
                    {formatInteger(data.views)}
                  </span>
                </td>
                <td className="py-3 pr-3 text-right">
                  <span title={formatInteger(data.reach)}>
                    {formatInteger(data.reach)}
                  </span>
                </td>
                <td className="py-3 pr-3 text-right">
                  <span title={formatInteger(data.tx)}>
                    {formatInteger(data.tx)}
                  </span>
                </td>
                <td className="py-3 pr-3 text-right">
                  <span title={formatInteger(data.likes)}>
                    {formatInteger(data.likes)}
                  </span>
                </td>
                <td className="py-3 pr-3 text-right">
                  <span title={formatInteger(data.comments)}>
                    {formatInteger(data.comments)}
                  </span>
                </td>
                <td className="py-3 pr-3 text-right">
                  <span title={formatInteger(data.saves)}>
                    {formatInteger(data.saves)}
                  </span>
                </td>
                <td className="py-3 pr-3 text-right">
                  <span title={formatInteger(data.shares)}>
                    {formatInteger(data.shares)}
                  </span>
                </td>
                <td className="py-3 text-right">
                  <span title={formatInteger(data.score)}>
                    {formatInteger(data.score)}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
